import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import '../consts/events.quiz-product-parent-link';

import { trackEvent } from '@zep/logger/tracking';

import type { ParentLinkPlacement } from '../consts/events.quiz-product-parent-link';
import {
  type NavParentKind,
  type ParentLinkFallback,
  resolveParentLink,
} from '../utils/navPath';

import { useNavigationPath } from './useNavigationPath';
import { useResolvedOwnership } from './useResolvedOwnership';

const PENDING = { status: 'pending' } as const;

/**
 * 목록 버튼의 라벨과 `<Link>` props. 이동은 router.push 가 아니라 `<Link>` 로 둔다.
 * 소유를 아직 모르면 `pending`.
 */
export function usePathParentLink(fallback?: ParentLinkFallback) {
  const { t } = useTranslation(['common']);
  const { pathname } = useRouter();
  const { path, labels } = useNavigationPath();
  const link = resolveParentLink({
    path,
    labels,
    isMine: useResolvedOwnership(),
    fallback,
  });

  const labelOf = (kind: NavParentKind, name: string) => {
    switch (kind) {
      case 'public':
        return t('common:quizDetail.browseExplore');
      case 'my_quiz':
        return t('common:quizDetail.myQuizList');
      case 'classrooms':
        return t('common:classroom.myClassrooms');
      case 'my_board':
        return t('common:myBoard');
      case 'featured':
        return t('common:category.popular');
      case 'my_folders':
        return t('common:folder.label.allMyFolders');
      case 'profile':
        return t('common:folder.profile.back', { name });
      case 'profile_folders':
        return t('common:folder.seo.ownerFolder', { ownerName: name });
      case 'folder':
        return name || t('common:folder.label.folders');
    }
  };

  if (link.status === 'pending') return PENDING;
  return {
    status: 'ready',
    label: labelOf(link.kind, link.name),
    getLinkProps: (placement: ParentLinkPlacement) => ({
      href: link.href,
      onClick: () =>
        trackEvent('quiz_product_parent_link_click', {
          target: link.kind,
          button: placement,
          place: pathname,
          is_from_origin: link.fromPath,
        }),
    }),
  } as const;
}
